/**
 * Configurações do usuário.
 *
 * A aba de voz traz um teste de microfone ao vivo. Sem ele, ajustar supressão
 * de ruído é adivinhação: a pessoa muda um interruptor, entra numa chamada e
 * pergunta "está melhor?" para os outros. Com o medidor e o retorno de áudio,
 * dá para ouvir o efeito na hora.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Mic, Monitor, Palette, ShieldCheck, User, Video, Volume2, Waves } from 'lucide-react'
import { SCREEN_SHARE_PRESETS } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api, ApiError } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { usePreferencesStore } from '@/store/preferences'
import { useVoiceStore } from '@/store/voice'
import {
  listarDispositivosDeVideo,
  type DispositivoDeVideo,
} from '@/lib/dispositivos-de-video'
import {
  MicrophoneProcessor,
  listAudioDevices,
  requestMicrophonePermission,
  type ProcessedAudio,
} from '@/lib/audio-processing'
import { Avatar, Button, Divider, Field, Input, Modal, Select, Slider, Switch } from '@/components/ui'

type Tab = 'voice' | 'appearance' | 'account'

export function SettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('voice')

  const tabs: { id: Tab; label: string; icon: typeof Mic }[] = [
    { id: 'voice', label: 'Voz e vídeo', icon: Mic },
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'account', label: 'Conta', icon: User },
  ]

  return (
    <Modal open={open} onClose={onClose} title="Configurações" size="lg">
      <div className="mb-5 flex gap-1 border-b border-line">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === id
                ? 'border-brand text-content-primary'
                : 'border-transparent text-content-secondary hover:text-content-primary',
            )}
          >
            <Icon className="size-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'voice' ? <VoiceTab active={open} /> : null}
      {tab === 'appearance' ? <AppearanceTab /> : null}
      {tab === 'account' ? <AccountTab /> : null}
    </Modal>
  )
}

function VoiceTab({ active }: { active: boolean }) {
  const audio = usePreferencesStore((s) => s.audio)
  const video = usePreferencesStore((s) => s.video)
  const setAudio = usePreferencesStore((s) => s.setAudio)
  const setVideo = usePreferencesStore((s) => s.setVideo)

  const inCall = useVoiceStore((s) => s.room !== null)
  const reapply = useVoiceStore((s) => s.reapplyAudioProcessing)
  const setOutputDevice = useVoiceStore((s) => s.setOutputDevice)

  const [devices, setDevices] = useState<{
    inputs: MediaDeviceInfo[]
    outputs: MediaDeviceInfo[]
    hasLabels: boolean
  }>({ inputs: [], outputs: [], hasLabels: false })

  /* Inclui placa de captura: para o navegador é só mais uma entrada de vídeo. */
  const [camerasDisponiveis, setCamerasDisponiveis] = useState<DispositivoDeVideo[]>([])

  const [testing, setTesting] = useState(false)
  const [level, setLevel] = useState(0)
  const [rnnoiseWorking, setRnnoiseWorking] = useState<boolean | null>(null)
  const [requestingPermission, setRequestingPermission] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)

  const processorRef = useRef<MicrophoneProcessor | null>(null)
  const handleRef = useRef<ProcessedAudio | null>(null)
  const timerRef = useRef<number | null>(null)

  const loadDevices = useCallback(async () => {
    setDevices(await listAudioDevices())
  }, [])

  /** Pede o acesso e recarrega a lista, que só ganha nomes depois disso. */
  const requestPermission = useCallback(async () => {
    setRequestingPermission(true)
    setPermissionDenied(false)
    try {
      const granted = await requestMicrophonePermission()
      if (!granted) setPermissionDenied(true)
      await loadDevices()
    } finally {
      setRequestingPermission(false)
    }
  }, [loadDevices])

  useEffect(() => {
    if (active) void loadDevices()
  }, [active, loadDevices])

  useEffect(() => {
    if (!active) return
    void listarDispositivosDeVideo().then((r) => setCamerasDisponiveis(r.dispositivos))
  }, [active])

  const stopTest = useCallback(async () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    await handleRef.current?.destroy()
    handleRef.current = null
    processorRef.current = null
    setTesting(false)
    setLevel(0)
  }, [])

  // Sair da tela com o microfone aberto deixaria a luz da webcam ou o ícone de
  // gravação ligados sem motivo.
  useEffect(() => {
    if (!active) void stopTest()
    return () => {
      void stopTest()
    }
  }, [active, stopTest])

  const startTest = async () => {
    const granted = await requestMicrophonePermission()
    if (!granted) return

    await loadDevices()

    const processor = new MicrophoneProcessor()
    processorRef.current = processor

    try {
      const handle = await processor.open(audio.inputDeviceId, audio)
      handleRef.current = handle
      setRnnoiseWorking(handle.rnnoiseActive)
      setTesting(true)

      timerRef.current = window.setInterval(() => setLevel(handle.getLevel()), 60)
    } catch {
      setRnnoiseWorking(false)
    }
  }

  /** Aplica a mudança na chamada em andamento, se houver. */
  const applyChange = async (patch: Partial<typeof audio>) => {
    setAudio(patch)
    const next = { ...audio, ...patch }
    if (inCall) await reapply(next)
    if (testing) {
      await stopTest()
      void startTest()
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section>
        <h3 className="mb-3 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          Dispositivos
        </h3>

        <div className="flex flex-col gap-3">
          <Field label="Microfone">
            <select
              value={audio.inputDeviceId ?? ''}
              onChange={(e) => void applyChange({ inputDeviceId: e.target.value || undefined })}
              className="h-10 w-full rounded-lg bg-surface-deepest px-3 outline-none"
            >
              <option value="">Padrão do sistema</option>
              {devices.inputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microfone ${device.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Saída de áudio">
            <select
              value={audio.outputDeviceId ?? ''}
              onChange={(e) => {
                setAudio({ outputDeviceId: e.target.value || undefined })
                if (e.target.value) void setOutputDevice(e.target.value)
              }}
              className="h-10 w-full rounded-lg bg-surface-deepest px-3 outline-none"
            >
              <option value="">Padrão do sistema</option>
              {devices.outputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Saída ${device.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
          </Field>

          {/*
            Sem permissão concedida, o navegador não revela quais dispositivos
            existem: a lista vem vazia ou sem nomes. Um aviso passivo não
            resolvia, porque a pessoa abria as configurações, via um seletor com
            só "Padrão do sistema" e concluía que o computador não tinha
            microfone. O botão dá o caminho num clique.
          */}
          {!devices.hasLabels ? (
            <div className="flex flex-col gap-2 rounded-lg bg-surface-base p-3">
              <p className="text-xs leading-relaxed text-content-secondary">
                O navegador só mostra seus microfones e alto-falantes depois que você autoriza o
                acesso. Nada é gravado nem enviado ao autorizar.
              </p>
              <Button
                size="sm"
                variant="secondary"
                loading={requestingPermission}
                onClick={() => void requestPermission()}
                className="self-start"
              >
                <Mic className="size-4" />
                Permitir acesso ao microfone
              </Button>
              {permissionDenied ? (
                <p className="text-xs text-danger">
                  O acesso foi negado. Libere o microfone para este site nas configurações do
                  navegador, no ícone de cadeado ao lado do endereço.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      <Divider />

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold tracking-wide text-content-secondary uppercase">
            Teste de microfone
          </h3>
          <Button size="xs" variant={testing ? 'danger' : 'secondary'} onClick={() => (testing ? void stopTest() : void startTest())}>
            {testing ? 'Parar teste' : 'Testar'}
          </Button>
        </div>

        <div className="rounded-lg bg-surface-base p-3">
          <div className="flex items-center gap-3">
            <Mic className={cn('size-5 shrink-0', testing ? 'text-success' : 'text-content-muted')} />

            {/* Barra segmentada: mais fácil de ler de relance do que uma barra
                contínua, e mostra claramente quando o sinal satura. */}
            <div className="flex h-3 flex-1 gap-0.5">
              {Array.from({ length: 24 }, (_, i) => {
                const threshold = (i + 1) / 24
                const lit = level >= threshold
                const isHot = threshold > 0.85
                return (
                  <span
                    key={i}
                    className={cn(
                      'flex-1 rounded-[1px] transition-colors duration-75',
                      lit
                        ? isHot
                          ? 'bg-danger'
                          : threshold > 0.6
                            ? 'bg-warning'
                            : 'bg-success'
                        : 'bg-surface-active',
                    )}
                  />
                )
              })}
            </div>
          </div>

          {testing ? (
            <p className="mt-2 text-xs text-content-muted">
              Fale normalmente. A barra deve chegar na faixa verde sem estourar no vermelho.
              {rnnoiseWorking === false
                ? ' A supressão por rede neural não pôde ser carregada neste navegador.'
                : rnnoiseWorking
                  ? ' Supressão por rede neural ativa.'
                  : ''}
            </p>
          ) : null}
        </div>
      </section>

      <Divider />

      <section>
        <h3 className="mb-1 flex items-center gap-2 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          <Waves className="size-4" />
          Tratamento de ruído
        </h3>
        <p className="mb-2 text-xs leading-relaxed text-content-muted">
          As camadas se complementam. O cancelamento de eco é do navegador e não tem substituto;
          a supressão por rede neural é o que remove teclado, ventilador e ruído da rua.
        </p>

        <Switch
          checked={audio.rnnoise}
          onChange={(v) => void applyChange({ rnnoise: v })}
          label="Supressão por rede neural"
          description="RNNoise em WebAssembly. Remove ruído constante mantendo a voz natural."
        />
        <Switch
          checked={audio.noiseGate}
          onChange={(v) => void applyChange({ noiseGate: v })}
          label="Porta de ruído"
          description="Silencia completamente entre as falas, cortando o resíduo de fundo."
        />

        {audio.noiseGate ? (
          <div className="pl-1">
            <Slider
              value={audio.noiseGateThreshold}
              onChange={(v) => setAudio({ noiseGateThreshold: v })}
              min={-70}
              max={-20}
              label="Sensibilidade da porta"
              formatValue={(v) => `${v} dB`}
            />
            <p className="-mt-1 text-xs text-content-muted">
              Valores mais altos cortam mais ruído, mas podem engolir o começo das frases.
            </p>
          </div>
        ) : null}

        <Divider className="my-3" />

        <Switch
          checked={audio.echoCancellation}
          onChange={(v) => void applyChange({ echoCancellation: v })}
          label="Cancelamento de eco"
          description="Impede que o som dos alto-falantes volte pelo microfone. Deixe ligado se não usa fone."
        />
        <Switch
          checked={audio.browserNoiseSuppression}
          onChange={(v) => void applyChange({ browserNoiseSuppression: v })}
          label="Supressão do navegador"
          description="Filtro espectral nativo. Leve, funciona junto com a rede neural."
        />
        <Switch
          checked={audio.autoGainControl}
          onChange={(v) => void applyChange({ autoGainControl: v })}
          label="Controle automático de ganho"
          description="Nivela o volume da sua voz automaticamente."
        />
        <Switch
          checked={audio.startMuted}
          onChange={(v) => setAudio({ startMuted: v })}
          label="Entrar silenciado"
          description="Ao entrar num canal de voz, o microfone começa desligado."
        />
      </section>

      <Divider />

      <section>
        <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          <Monitor className="size-4" />
          Compartilhamento de tela
        </h3>

        <Field label="Perfil padrão">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {SCREEN_SHARE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setVideo({ defaultScreenPreset: preset.id })}
                className={cn(
                  'rounded-lg border p-2.5 text-left transition-colors',
                  video.defaultScreenPreset === preset.id
                    ? 'border-brand bg-brand-subtle'
                    : 'border-line bg-surface-base hover:border-line-strong',
                )}
              >
                <p className="text-[13px] font-medium text-content-primary">{preset.label}</p>
                <p className="mt-0.5 text-[11px] text-content-muted">
                  {preset.height}p · {preset.framerate} fps
                </p>
              </button>
            ))}
          </div>
        </Field>

        <Switch
          checked={video.shareSystemAudio}
          onChange={(v) => setVideo({ shareSystemAudio: v })}
          label="Compartilhar áudio do sistema"
          description="Inclui o som do que está na tela. Depende de suporte do navegador."
        />
      </section>

      <Divider />

      <section>
        <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          <Video className="size-4" />
          Câmera
        </h3>

        <Field
          label="Dispositivo"
          hint="Placa de captura também aparece aqui, e dá para transmiti-la como tela pelo botão de compartilhar."
        >
          <Select
            value={video.inputDeviceId ?? ''}
            onChange={(e) => setVideo({ inputDeviceId: e.target.value || undefined })}
          >
            <option value="">Padrão do sistema</option>
            {camerasDisponiveis.map((dispositivo) => (
              <option key={dispositivo.deviceId} value={dispositivo.deviceId}>
                {dispositivo.label}
              </option>
            ))}
          </Select>
        </Field>

        <Switch
          checked={video.espelharCamera}
          onChange={(v) => setVideo({ espelharCamera: v })}
          label="Espelhar minha imagem"
          description="Inverte só a sua prévia, como num espelho. Desligue se a entrada for uma placa de captura: os outros sempre veem a imagem como ela é."
        />
      </section>
    </div>
  )
}

function AppearanceTab() {
  const ui = usePreferencesStore((s) => s.ui)
  const setUi = usePreferencesStore((s) => s.setUi)

  return (
    <div className="flex flex-col gap-4">
      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          Mensagens
        </h3>

        <Switch
          checked={ui.compactMessages}
          onChange={(v) => setUi({ compactMessages: v })}
          label="Modo compacto"
          description="Reduz o espaçamento entre mensagens, cabendo mais na tela."
        />
        <Switch
          checked={ui.sendOnEnter}
          onChange={(v) => setUi({ sendOnEnter: v })}
          label="Enviar com Enter"
          description="Desligado, Enter quebra linha e Ctrl+Enter envia."
        />
      </section>

      <Divider />

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          Interface
        </h3>

        <Switch
          checked={ui.showMemberList}
          onChange={(v) => setUi({ showMemberList: v })}
          label="Lista de membros"
          description="Mostra a coluna da direita com quem está no servidor."
        />
        <Switch
          checked={ui.voiceSounds}
          onChange={(v) => setUi({ voiceSounds: v })}
          label="Sons de entrada e saída"
          description="Toca um aviso curto quando alguém entra ou sai da chamada."
        />
        <Switch
          checked={ui.reduceMotion}
          onChange={(v) => setUi({ reduceMotion: v })}
          label="Reduzir animações"
          description="Desativa transições além do que o sistema já pede."
        />
      </section>
    </div>
  )
}

function AccountTab() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const logout = useAuthStore((s) => s.logout)

  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null)

  const fileRef = useRef<HTMLInputElement>(null)

  if (!user) return null

  const handleSaveProfile = async () => {
    setSaving(true)
    setError(null)
    try {
      const updated = await api.users.updateMe({ displayName: displayName.trim() })
      setUser(updated)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatar = async (file: File) => {
    try {
      const { avatarUrl } = await api.users.uploadAvatar(file)
      setUser({ ...user, avatarUrl })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível enviar a imagem')
    }
  }

  const handleChangePassword = async () => {
    setPasswordMessage(null)
    try {
      await api.auth.changePassword(currentPassword, newPassword)
      setPasswordMessage('Senha alterada. As outras sessões foram encerradas.')
      setCurrentPassword('')
      setNewPassword('')
    } catch (err) {
      setPasswordMessage(err instanceof ApiError ? err.message : 'Não foi possível alterar')
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex items-center gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} className="group relative">
          <Avatar userId={user.id} name={user.displayName} src={user.avatarUrl} size="xl" />
          <span className="revelar-no-hover absolute inset-0 flex items-center justify-center rounded-full bg-black/60 text-[11px] font-medium text-white">
            Trocar
          </span>
        </button>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void handleAvatar(file)
            e.target.value = ''
          }}
        />

        <div className="min-w-0 flex-1">
          <p className="text-lg font-semibold text-content-primary">{user.displayName}</p>
          <p className="text-sm text-content-muted">@{user.username}</p>
          <p className="mt-0.5 text-xs text-content-muted">{user.email}</p>
          {user.isAdmin ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-warning-subtle px-2 py-0.5 text-[11px] font-medium text-warning">
              <ShieldCheck className="size-3" />
              Administrador
            </span>
          ) : null}
        </div>
      </section>

      <Divider />

      <section className="flex flex-col gap-3">
        <Field label="Nome de exibição" error={error ?? undefined}>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={48} />
        </Field>

        <div className="flex justify-end">
          <Button
            onClick={() => void handleSaveProfile()}
            loading={saving}
            disabled={displayName.trim() === user.displayName}
          >
            Salvar
          </Button>
        </div>
      </section>

      <Divider />

      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold tracking-wide text-content-secondary uppercase">
          Alterar senha
        </h3>

        <Field label="Senha atual">
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </Field>

        <Field label="Nova senha" hint="Mínimo de 8 caracteres">
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>

        {passwordMessage ? (
          <p className="text-sm text-content-secondary">{passwordMessage}</p>
        ) : null}

        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={() => void handleChangePassword()}
            disabled={!currentPassword || newPassword.length < 8}
          >
            Alterar senha
          </Button>
        </div>
      </section>

      <Divider />

      <div className="flex justify-between">
        <span className="text-sm text-content-muted">
          <Volume2 className="mr-1 inline size-4" />
          Sair encerra apenas esta sessão.
        </span>
        <Button variant="danger" onClick={() => void logout()}>
          Sair da conta
        </Button>
      </div>
    </div>
  )
}
